import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Layout from "./layout/Layout";
import BookingStepHeader from "./bookingForm/BookingStepHeader";
import BookingInfoInline from "./bookingForm/BookingInfoInline";
import PageStateGuard from "./shared/PageStateGuard";
import PaymentProcessorSwitch from "./shared/PaymentProcessorSwitch";
import CreditCardDisabledNotice from "./shared/CreditCardDisabledNotice";
import AlertBanner from "./shared/AlertBanner";
import { useBooking } from "./hooks/useBooking";
import { notifyBugsnag } from "../bugsnag";
import { redirectToOldGuestExperience } from "../utils/redirectToOldExperience";
import { canUseStripeConnect } from "../utils/paymentProcessorUtils";
import { isCreditCardPaymentDisabledForBooking } from "../utils/paymentHelpers";
import { formatDate, formatTime } from "../../utils/countryLocaleMap";
import { useAnalyticsConfiguration } from "./hooks/useAnalytics";
import analytics, { readPartnerAnalyticsParams } from "../analytics";

interface PaymentContentOptions {
  readonly bookingData: any;
  readonly canShowStripeConnect: boolean;
  readonly paymentBookingData: any;
  readonly bookingId: string;
  readonly t: (key: string) => string;
  readonly onPaymentSuccess: (email?: string, amount?: number) => void;
  /** The venue only accepts debit cards for this booking type. */
  readonly creditCardsDisabled: boolean;
}

/**
 * The two outcomes that leave nothing to pay for. Stated as the flow's other
 * banners are, so the design's stylesheets reach them through the marker the
 * variant carries: these were hand-rolled `role="alert"` divs, which no
 * marker and therefore no repaint applied to.
 *
 * Notices can contain one or more blocks rather than using a `<br>`.
 * AlertBanner wraps the content in a span inside a flex row, so that span is
 * made a block too — left inline, multiple lines would resolve against a row
 * rather than stacking.
 */
function renderNoPaymentDueNotice(variant: "danger" | "warning", lines: readonly string[]): React.ReactNode {
  return (
    <AlertBanner variant={variant} textClassName="gcw:block">
      {lines.map((line) => (
        <span key={line} className="gcw:block">
          {line}
        </span>
      ))}
    </AlertBanner>
  );
}

function renderPaymentContent({
  bookingData,
  canShowStripeConnect,
  paymentBookingData,
  bookingId,
  t,
  onPaymentSuccess,
  creditCardsDisabled,
}: PaymentContentOptions): React.ReactNode {
  if (bookingData.status === "rejected") {
    return renderNoPaymentDueNotice("danger", [t("booking_cancelled"), t("booking_cancelled_no_payment")]);
  }

  if (!bookingData.depositTotal) {
    return renderNoPaymentDueNotice("warning", [`${t("already_paid")} ${t("no_further_payment")}`]);
  }

  return (
    <div id="gc-syb-payment-processor">
      {creditCardsDisabled && <CreditCardDisabledNotice />}
      <PaymentProcessorSwitch
        useStripeConnect={canShowStripeConnect}
        stripeProps={{
          bookingData: paymentBookingData,
          bookingId,
          showInfo: true,
          mode: "successOnly",
          disableCreditCards: creditCardsDisabled,
        }}
        freedomPayProps={{
          bookingId,
          bookingData: paymentBookingData,
          onFinish: () => {},
          onPaymentSuccess,
          mode: "successOnly",
          showInfo: true,
        }}
      />
    </div>
  );
}

interface SecureYourBookingProps {
  readonly bookingId: string;
}

function SecureYourBooking(props: SecureYourBookingProps) {
  const { bookingData, loading, error, unavailableMessage } = useBooking(props.bookingId);
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [hasAttemptedRedirect, setHasAttemptedRedirect] = useState(false);

  useAnalyticsConfiguration({
    ...readPartnerAnalyticsParams(),
    venue: bookingData
      ? { id: props.bookingId, title: bookingData.venue.title, currency: bookingData.venue.currency }
      : undefined,
  });

  // `purchase` — FreedomPay confirms in page, so the deposit is only ever a
  // conversion here. Stripe Connect redirects and is tracked by ConfirmPayment.
  const handlePaymentSuccess = useCallback(
    (_email?: string, amount?: number) => {
      void analytics.purchase({
        id: props.bookingId,
        // An existing booking is its own payment journey, so initiation and
        // conversion already share this id.
        journeyId: props.bookingId,
        typeName: bookingData?.bookingTypeName,
        numPeople: bookingData?.guestCount,
        value: amount ?? parseDeposit(bookingData?.depositTotal ?? null),
        currency: bookingData?.venue.currency,
        status: bookingData?.status,
      });
    },
    [props.bookingId, bookingData],
  );

  // Helper to handle redirect and error reporting
  const handleRedirectWithBugsnag = useCallback(async () => {
    if (hasAttemptedRedirect) return;
    setHasAttemptedRedirect(true);
    try {
      setIsRedirecting(true);
      await redirectToOldGuestExperience();
    } catch (error) {
      notifyBugsnag(new Error("Failed to redirect to old guest experience"), {
        component: "secureYourBooking",
        bookingId: props.bookingId,
        timestamp: new Date().toISOString(),
        error,
      });
    }
  }, [hasAttemptedRedirect, props.bookingId]);

  // Check if a valid payment processor is configured
  const validPaymentProcessors = ["stripe_connect", "freedom_pay"];
  const hasValidPaymentProcessor =
    !!bookingData?.paymentProcessor && validPaymentProcessors.includes(bookingData.paymentProcessor);

  // Handle redirects in useEffect to avoid state updates during render
  useEffect(() => {
    if (!bookingData || hasAttemptedRedirect) return;
    if (bookingData.status === "rejected") return;

    const shouldRedirectLegacy =
      bookingData.venue.collins_features &&
      typeof bookingData.venue.collins_features === "object" &&
      !Array.isArray(bookingData.venue.collins_features) &&
      (bookingData.venue.collins_features as { redirect_old_guest_experience?: boolean }).redirect_old_guest_experience;

    const shouldRedirectNoProcessor = bookingData.depositTotal && !hasValidPaymentProcessor;

    if (shouldRedirectLegacy || shouldRedirectNoProcessor) {
      void handleRedirectWithBugsnag();
    }
  }, [bookingData, hasValidPaymentProcessor, hasAttemptedRedirect, handleRedirectWithBugsnag]);

  // Defensive fallback for StripeConnect: only pass if all required fields are present
  const canShowStripeConnect = !!bookingData?.depositTotal && canUseStripeConnect(bookingData ?? {});

  // StripeConnect expects venue as a string, not an object
  const paymentBookingData = bookingData
    ? {
        ...bookingData,
        depositTotal: parseDeposit(bookingData.depositTotal),
        venue: {
          title: bookingData.venue.title,
          currency: bookingData.venue.currency,
          country_code: bookingData.venue.country_code,
          collins_features: bookingData.venue.collins_features,
        },
      }
    : null;

  if (loading || isRedirecting || error || unavailableMessage || !bookingData) {
    return (
      <PageStateGuard
        loading={loading || isRedirecting}
        error={error}
        refusalMessage={unavailableMessage}
        data={bookingData}
        showHeader
      >
        {null}
      </PageStateGuard>
    );
  }

  const countryCode = bookingData.venue.country_code ?? "GB";
  const formattedDate =
    bookingData.date && typeof bookingData.date === "string"
      ? formatDate(bookingData.date, countryCode, "short")
      : undefined;
  const formattedTime =
    bookingData.time && bookingData.date && typeof bookingData.date === "string"
      ? formatTime(bookingData.time, bookingData.date, countryCode)
      : undefined;

  return (
    <div id="gc-syb-page">
      <Layout
        showHeader
        mainClassName="no-aside"
        main={
          <>
            <div id="gc-syb-step-header" className="gcw:hidden gcw:lg:block">
              <BookingStepHeader venueName={bookingData.venue.title} />
            </div>
            <BookingInfoInline
              venueName={bookingData.venue.title}
              bookingDate={formattedDate}
              bookingTime={formattedTime}
              covers={String(bookingData.guestCount)}
              bookingTypeName={bookingData.bookingTypeName}
            />
            <div id="gc-syb-payment-content" className="app-main">
              {renderPaymentContent({
                bookingData,
                canShowStripeConnect,
                paymentBookingData: paymentBookingData!,
                bookingId: props.bookingId,
                t,
                onPaymentSuccess: handlePaymentSuccess,
                creditCardsDisabled: isCreditCardPaymentDisabledForBooking(bookingData),
              })}
            </div>
          </>
        }
        aside={null}
      />
    </div>
  );
}

function parseDeposit(deposit: string | null): number {
  if (!deposit) return 0;
  const regex = /£?([\d,]+\.?\d*)/;
  const match = regex.exec(deposit);
  return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
}

export default SecureYourBooking;
